import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Sparkles } from 'lucide-react';

import { Button } from '@domcrm/embed-ui';

import { postCompletePasswordReset } from '../widget-api/auth-password-reset';
import { ApiError } from '../widget-api/http-client';
import { useWidgetSeo } from '../widget-hooks/use-widget-seo';
import { PATH } from '../widget-lib/path-routes';
import { generateEmbedPassword } from '../widget-lib/generate-embed-password';
import { EMBED_MIN_PASSWORD_LENGTH, validateNewPassword } from '../widget-lib/validation';
import { EmbedMessageBlock } from '../widget-ui/embed-message-block';
import { PromoBanner } from '../widget-ui/promo-banner';
import { useSmartCaptcha } from '../widget-lib/smartcaptcha';
import { SmartCaptchaNotice } from '../widget-ui/smart-captcha-notice';

const inputClass =
  'min-h-10 w-full rounded-sm border-0 bg-[var(--bg-soft)] px-3 py-2 text-base text-[var(--text)] focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)] sm:min-h-8 sm:py-1 sm:text-[13px]';
const inputClassWithTrailing =
  'min-h-10 w-full rounded-sm border-0 bg-[var(--bg-soft)] py-2 pl-3 pr-11 text-base text-[var(--text)] focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)] sm:min-h-8 sm:py-1 sm:text-[13px]';

export default function ResetPasswordPage() {
  useWidgetSeo(
    'Сброс пароля',
    'Установка нового пароля для доступа в личный кабинет.'
  );
  const [searchParams] = useSearchParams();
  const qs = searchParams.toString();
  const suffix = qs ? `?${qs}` : '';

  const token = useMemo(() => (searchParams.get('token') ?? '').trim(), [searchParams]);

  const [currentPw, setCurrentPw] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Yandex SmartCaptcha (невидимая): включается бэкендом (SMARTCAPTCHA_MODE=required).
  const captcha = useSmartCaptcha();

  const canSubmit = useMemo(() => {
    if (loading) return false;
    if (!token) return false;
    const p1 = validateNewPassword(pw, { required: true });
    if (p1 !== true) return false;
    if (pw2.trim().length < EMBED_MIN_PASSWORD_LENGTH) return false;
    if (pw !== pw2) return false;
    return true;
  }, [token, loading, pw, pw2]);

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    if (!token) {
      setError('В ссылке нет токена. Запросите сброс пароля снова.');
      return;
    }
    const pErr = validateNewPassword(pw, { required: true });
    if (pErr !== true) {
      setError(pErr);
      return;
    }
    if (pw !== pw2) {
      setError('Пароли не совпадают.');
      return;
    }
    setLoading(true);
    try {
      await captcha.run((captchaToken) =>
        postCompletePasswordReset({
          token,
          new_password: pw,
          new_password_confirm: pw2,
          current_password: currentPw.trim() ? currentPw.trim() : null,
          ...(captchaToken ? { captcha_token: captchaToken } : {})
        })
      );
      setDone(true);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Не удалось сменить пароль.';
      setError(msg);
      if (captcha.enabled) captcha.reset();
    } finally {
      setLoading(false);
    }
  }

  if (!token) {
    return (
      <div className="mx-auto w-full max-w-md space-y-4 w-full min-w-0">
        <h1 className="text-xl font-semibold tracking-tight">Сброс пароля</h1>
        <p className="text-sm text-[var(--text-muted)]">Откройте ссылку из письма полностью (с параметром token).</p>
        <Link className="text-[var(--accent)] underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]" to={`${PATH.forgotPassword}${suffix}`}>
          Запросить ссылку снова
        </Link>
      </div>
    );
  }

  if (done) {
    return (
      <div className="mx-auto w-full max-w-md space-y-4 w-full min-w-0">
        <h1 className="text-xl font-semibold tracking-tight">Пароль обновлён</h1>
        <p className="text-sm text-[var(--text-muted)]">Войдите с новым паролем.</p>
        <Link className="inline-flex text-[var(--accent)] underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]" to={`${PATH.signIn}${suffix}`}>
          Перейти ко входу
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-md space-y-4 w-full min-w-0">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Новый пароль</h1>
        <PromoBanner screen="auth" />
        <p className="mt-1 text-sm text-[var(--text-muted)]">Задайте пароль по политике сложности (как при регистрации).</p>
      </div>
      <form className="space-y-4" onSubmit={(e) => void onSubmit(e)} noValidate>
        <div>
          <label className="mb-1.5 block text-[13px] font-medium text-[var(--text)]" htmlFor="reset-current-pw">
            Текущий пароль
          </label>
          <input
            id="reset-current-pw"
            type="password"
            autoComplete="current-password"
            className={inputClass}
            value={currentPw}
            onChange={(ev) => setCurrentPw(ev.target.value)}
          />
          <p className="mt-1 text-xs text-[var(--text-muted)]">
            Если вы открыли ссылку из письма после запроса в личном кабинете — укажите текущий пароль. Для восстановления
            по «Забыли пароль» поле можно оставить пустым.
          </p>
        </div>
        <div>
          <label className="mb-1.5 block text-[13px] font-medium text-[var(--text)]" htmlFor="reset-pw">
            Новый пароль
          </label>
          <div className="relative">
            <input
              id="reset-pw"
              type="password"
              autoComplete="new-password"
              className={inputClassWithTrailing}
              onChange={(ev) => setPw(ev.target.value)}
            />
            <button
              type="button"
              className="absolute right-1 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-sm text-[var(--accent)] hover:bg-[var(--bg-elevated)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
              onClick={() => {
                const g = generateEmbedPassword();
                setPw(g);
                setPw2('');
              }}
              aria-label="Сгенерировать пароль"
              title="Сгенерировать пароль"
            >
              <Sparkles className="h-4 w-4" strokeWidth={2} aria-hidden />
            </button>
          </div>
        </div>
        <div>
          <label className="mb-1.5 block text-[13px] font-medium text-[var(--text)]" htmlFor="reset-pw2">
            Повтор пароля
          </label>
          <input
            id="reset-pw2"
            type="password"
            autoComplete="new-password"
            className={inputClass}
            value={pw2}
            onChange={(ev) => setPw2(ev.target.value)}
          />
        </div>
        {error ? (
          <EmbedMessageBlock variant="error" size="field" className="w-full" role="alert" aria-live="assertive">
            {error}
          </EmbedMessageBlock>
        ) : null}
        <SmartCaptchaNotice
          serverEnabled={captcha.serverEnabled}
          challengeVisible={captcha.challengeVisible}
          status={captcha.status}
          onRetry={captcha.refresh}
        />
        <Button type="submit" className="w-full min-h-11 sm:min-h-9" disabled={!canSubmit || loading || captcha.loading}>
          {loading ? 'Сохраняем…' : 'Сохранить пароль'}
        </Button>
      </form>
    </div>
  );
}
